import { Button } from '@/ui-kit'
import { MQ_BREAKPOINTS_DOWN, useMediaQuery } from '@/ui-kit/media'
import { Plural, Trans, useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { ChevronLeft, ChevronRight } from 'lucide-react'

type PaginationProps = {
  page: number
  pageCount: number
  total: number
  onPageChange: (page: number) => void
  className?: string
}

const ELLIPSIS = '…' as const

const buildPageItems = (page: number, pageCount: number, siblingCount: number): (number | typeof ELLIPSIS)[] => {
  if (pageCount <= 1) return [1]

  const start = Math.max(2, page - siblingCount)
  const end = Math.min(pageCount - 1, page + siblingCount)

  const items: (number | typeof ELLIPSIS)[] = [1]
  if (start > 2) items.push(ELLIPSIS)
  for (let p = start; p <= end; p++) items.push(p)
  if (end < pageCount - 1) items.push(ELLIPSIS)
  if (pageCount > 1) items.push(pageCount)

  return items
}

export const Pagination = ({ page, pageCount, total, onPageChange, className }: PaginationProps) => {
  const { t } = useLingui()
  const isMobile = useMediaQuery(MQ_BREAKPOINTS_DOWN.smallDown)
  const siblingCount = isMobile ? 0 : 1

  if (pageCount <= 1) return null

  const items = buildPageItems(page, pageCount, siblingCount)
  const isFirst = page <= 1
  const isLast = page >= pageCount
  const rangeStart = (page - 1) * 10 + 1
  const rangeEnd = Math.min(page * 10, total)

  return (
    <div className={clsx('flex flex-wrap items-center justify-between gap-2', className)}>
      <nav className={clsx('flex items-center justify-center gap-2')} aria-label={t`Pagination`}>
        <Button
          size="icon"
          variant="bordered-gray"
          isDisabled={isFirst}
          onClick={() => onPageChange(page - 1)}
          title={t`Previous page`}
        >
          <ChevronLeft size={18} className="shrink-0" />
        </Button>
        <ul className="flex items-center gap-1">
          {items.map((item, idx) =>
            item === ELLIPSIS ? (
              <li
                key={`ellipsis-${idx}`}
                className="text-gray-80 flex h-9 w-9 items-center justify-center text-sm select-none"
              >
                {ELLIPSIS}
              </li>
            ) : (
              <li key={item}>
                <Button
                  size="small"
                  variant={item === page ? 'bordered' : 'bordered-gray'}
                  onClick={() => onPageChange(item)}
                  className="min-w-9 px-3"
                >
                  {item}
                </Button>
              </li>
            )
          )}
        </ul>
        <Button
          size="icon"
          variant="bordered-gray"
          isDisabled={isLast}
          onClick={() => onPageChange(page + 1)}
          title={t`Next page`}
        >
          <ChevronRight size={18} className="shrink-0" />
        </Button>
      </nav>
      <span className="text-gray-90 max-mobile:hidden select-none">
        <Trans>
          {rangeStart}-{rangeEnd} of <Plural value={total} one="# item" other="# items" />
        </Trans>
      </span>
    </div>
  )
}
